import React, { useEffect, useState } from 'react'
import { Button } from '@chakra-ui/react'
import { Tooltip } from '@chakra-ui/react'
import { Collapse } from '@chakra-ui/react'
import { Box, Text, Flex } from '@chakra-ui/react'
import { Radio, RadioGroup } from '@chakra-ui/react'

import SharedLabel from '~components/shared/form/shared-label'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedHelpText from '~components/shared/form/shared-help-text'
import {
    ChatFieldsFragment,
    ClientPhoneFieldsFragment,
    MessageMethod,
} from '~graphql/generated/graphql'
import { getPhoneCountryFromCode } from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import { Icon } from '@chakra-ui/icons'
import { SetStateFn } from '~config/constants'
import { FaAngleDown, FaAngleUp } from 'react-icons/fa6'

interface Props {
    chat: ChatFieldsFragment
    messageMethods: MessageMethod[]
    availablePhones: ClientPhoneFieldsFragment[]
    clientPhone: ClientPhoneFieldsFragment | undefined
    setClientPhone: SetStateFn<ClientPhoneFieldsFragment | undefined>
    baseClientPhone: string | null | undefined
}

export default function ClientPhoneSelect({
    chat,
    messageMethods,
    availablePhones,
    clientPhone,
    setClientPhone,
    baseClientPhone,
}: Props) {
    const [manuallyHide, setManuallyHide] = useState<boolean>(false)

    useEffect(() => {
        if (messageMethods.includes(MessageMethod.Sms)) {
            setManuallyHide(false)
        }
    }, [messageMethods])

    return (
        <>
            <Box
                as={Collapse}
                in={messageMethods.includes(MessageMethod.Sms)}
                w="full"
            >
                <SharedStack mt={2} spacing={2}>
                    <Flex
                        w="full"
                        flexDir="row"
                        alignItems="center"
                        justifyContent="space-between"
                        pl={2}
                    >
                        <SharedLabel fontSize="xs">
                            Send to{' '}
                            {!manuallyHide
                                ? '...'
                                : clientPhone?.phone
                                  ? formatPhoneReadable(
                                        clientPhone.phone,
                                        clientPhone.country_code
                                            ? getPhoneCountryFromCode(
                                                  clientPhone.country_code,
                                              )
                                            : chat.clinic?.address?.country,
                                    )
                                  : baseClientPhone
                                    ? formatPhoneReadable(
                                          baseClientPhone,
                                          chat.clinic?.address?.country,
                                      )
                                    : 'No phone selected'}
                        </SharedLabel>
                        <Button
                            size="xs"
                            variant="link"
                            onClick={() => setManuallyHide(!manuallyHide)}
                        >
                            <SharedStack row fit spacing={1}>
                                <Text>{manuallyHide ? 'Show' : 'Hide'}</Text>
                                <Icon
                                    as={manuallyHide ? FaAngleDown : FaAngleUp}
                                />
                            </SharedStack>
                        </Button>
                    </Flex>
                    <Box as={Collapse} in={!manuallyHide}>
                        <RadioGroup
                            value={clientPhone?.phone}
                            onChange={e => {
                                const phone = availablePhones.find(
                                    p => p.phone === e,
                                )
                                setClientPhone(phone)
                            }}
                        >
                            <SharedStack>
                                {availablePhones.length ? (
                                    availablePhones.map(phone => (
                                        <PhoneOption
                                            key={phone.phone}
                                            chat={chat}
                                            phone={phone}
                                        />
                                    ))
                                ) : (
                                    <BasicPhoneOption
                                        chat={chat}
                                        phone={baseClientPhone || ''}
                                    />
                                )}
                            </SharedStack>
                        </RadioGroup>
                    </Box>
                </SharedStack>
            </Box>
        </>
    )
}

interface PhoneOptionProps {
    chat: ChatFieldsFragment
    phone: ClientPhoneFieldsFragment
}

function PhoneOption({ chat, phone }: PhoneOptionProps) {
    return (
        <Tooltip
            isDisabled={true}
            label="This phone is not available for SMS communication."
            bg="white"
            borderColor="blackAlpha.200"
            borderWidth={1}
            fontSize="12px"
            color="gray.700"
            rounded="lg"
            hasArrow
        >
            <Box>
                <Radio
                    value={phone.phone}
                    w="full"
                    colorScheme="brand"
                    py={1}
                    px={2}
                >
                    <Box>
                        <Text fontSize="sm" whiteSpace="nowrap">
                            {formatPhoneReadable(
                                phone.phone,
                                !!phone.country_code
                                    ? getPhoneCountryFromCode(
                                          phone.country_code ?? '',
                                      )
                                    : chat.clinic?.address?.country,
                            )}
                        </Text>
                        <SharedHelpText fontSize="xs">
                            {phone.name}
                        </SharedHelpText>
                    </Box>
                </Radio>
            </Box>
        </Tooltip>
    )
}

function BasicPhoneOption({
    phone,
    chat,
}: {
    phone: string
    chat: ChatFieldsFragment
}) {
    return (
        <Tooltip
            isDisabled={true}
            label="This phone is not available for SMS communication."
            bg="white"
            borderColor="blackAlpha.200"
            borderWidth={1}
            fontSize="12px"
            color="gray.700"
            rounded="lg"
            hasArrow
        >
            <Box>
                <Radio value={phone} w="full" colorScheme="brand" py={1} px={2}>
                    <Box>
                        <Text fontSize="sm" whiteSpace="nowrap">
                            {formatPhoneReadable(
                                phone,
                                chat.clinic?.address?.country,
                            )}
                        </Text>
                    </Box>
                </Radio>
            </Box>
        </Tooltip>
    )
}
